export const formatDate = (date: Date): string => {
  const pad = (num: number) => String(num).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}`;
};

export const formatRangeString = (period: string, start?: Date, end?: Date): string => {
  const shortcuts: Record<string, string> = { last24h: '24h', last30d: '30d' };
  return shortcuts[period] || (start && end ? `${formatDate(start)}:${formatDate(end)}` : '');
};

export const getInclusiveRangeStart = (anchor: Date, totalDays: number): Date => {
  const start = new Date(anchor);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (totalDays - 1));
  return start;
};

const FIXED_PERIOD_DAY_COUNTS: Record<string, number> = {
  last7d: 7,
  last30d: 30,
  last3m: 90,
  last360d: 360,
};

export const getCalculatedDates = (period: string) => {
  const start = new Date();
  const end = new Date();
  const now = new Date();

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  const fixedDayCount = FIXED_PERIOD_DAY_COUNTS[period];
  if (fixedDayCount !== undefined) {
    return { start: getInclusiveRangeStart(end, fixedDayCount), end };
  }

  const specialStrategies: Record<string, () => void> = {
    today: () => {},
    yesterday: () => {
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
    },
    last24h: () => {
      start.setDate(now.getDate() - 1);
      start.setHours(now.getHours(), now.getMinutes(), 0, 0);
      end.setHours(now.getHours(), now.getMinutes(), 59, 999);
    },
    current_week: () => start.setDate(start.getDate() - start.getDay()),
    current_month: () => start.setDate(1),
    last_month: () => {
      start.setDate(1);
      start.setMonth(start.getMonth() - 1);
      end.setDate(0);
    },
    whole_period: () => {
      start.setFullYear(now.getFullYear() - 10, 0, 1);
      start.setHours(0, 0, 0, 0);
      end.setHours(now.getHours(), now.getMinutes(), 59, 999);
    },
  };

  if (specialStrategies[period]) {
    specialStrategies[period]();
    return { start, end };
  }

  return null;
};

export const RESTRICTED_MAX_RANGE_DAYS = 90;

export const clampToMaxRangeDays = (start: Date, end: Date, maxDays: number): { start: Date; end: Date } => {
  const maxMs = maxDays * 24 * 60 * 60 * 1000;
  if (end.getTime() - start.getTime() <= maxMs) return { start, end };
  return { start: getInclusiveRangeStart(end, maxDays), end };
};

const WHOLE_PERIOD_YEARS_BACK = 10;

const isWholePeriodStart = (date: Date): boolean => {
  const yearsBack = new Date().getFullYear() - date.getFullYear();
  return Math.abs(yearsBack - WHOLE_PERIOD_YEARS_BACK) <= 1
    && date.getMonth() === 0 && date.getDate() === 1
    && date.getHours() === 0 && date.getMinutes() === 0;
};

export const parseDateRange = (
  dateRange: string
): { dataRange: string; startDate?: Date; endDate?: Date } | null => {
  const reverseShortcuts: Record<string, string> = { '24h': 'last24h', '7d': 'last7d', '30d': 'last30d' };

  if (reverseShortcuts[dateRange]) {
    const dataRange = reverseShortcuts[dateRange];
    const dates = getCalculatedDates(dataRange);
    return { dataRange, startDate: dates?.start, endDate: dates?.end };
  }

  const customMatch = /^(\d{12}):(\d{12})$/.exec(dateRange);
  if (customMatch) {
    const parseCustomDate = (str: string) => {
      const y = Number(str.slice(0, 4));
      const mo = Number(str.slice(4, 6)) - 1;
      const d = Number(str.slice(6, 8));
      const h = Number(str.slice(8, 10));
      const m = Number(str.slice(10, 12));
      return new Date(y, mo, d, h, m);
    };

    const startDate = parseCustomDate(customMatch[1]);
    const endDate = parseCustomDate(customMatch[2]);

    if (isWholePeriodStart(startDate)) {
      return { dataRange: 'whole_period', startDate, endDate };
    }

    const periodsToCheck = [
      'today', 'yesterday', 'current_week', 'current_month', 'last_month',
      'last7d', 'last30d', 'last3m', 'last360d',
    ];

    for (const period of periodsToCheck) {
      const dates = getCalculatedDates(period);
      if (dates && formatDate(dates.start) === customMatch[1] && formatDate(dates.end) === customMatch[2]) {
        return { dataRange: period, startDate: dates.start, endDate: dates.end };
      }
    }

    return { dataRange: 'custom', startDate, endDate };
  }

  return null;
};